import React, {useEffect, useState} from "react";
import UserTable from "./UserTable";
import UserModal from "./UserModal";
import ResetPasswordModal from "./ResetPasswordModal";

import {fetchUsers, editUser} from "../../api/user";
import {fetchRoles} from "../../api/role";
import {fetchCities} from "../../api/city";
import {fetchProducts} from "../../api/product";

import User from "../../types/User";
import EntityInfo from "../../types/EntityInfo";
import {operationNames, userHasAccess} from "../../data/pages";

const initialUserState = {
    id: 0,
    firstname: '',
    lastname: '',
    active: false,
    username: '',
    roleIds: [],
    cityIds: [],
    productIds: []
};

const Users = (): JSX.Element => {
    const [users, setUsers] = useState<User[]>([]);
    const [roles, setRoles] = useState<EntityInfo[]>([]);
    const [cities, setCities] = useState<EntityInfo[]>([]);
    const [products, setProducts] = useState<EntityInfo[]>([]);

    const [open, setOpen] = useState(false);
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(initialUserState);
    const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null);

    const canEdit = userHasAccess(operationNames.EDIT_USER);

    const getUsers = () => {
        fetchUsers().then(response => setUsers(response?.users || []));
    };

    const getRoles = () => {
        fetchRoles().then(r => setRoles(r?.items || []));
    };

    const getCities = () => {
        fetchCities().then(r => setCities(r?.items || []));
    };

    const getProducts = () => {
        fetchProducts().then(r => setProducts(r?.items || []));
    };

    useEffect(() => {
        getUsers();
        getRoles();
        getCities();
        getProducts();
    }, []);

    const onEditClick = (user: User) => {
        setSelectedUser({
            id: user.id,
            username: user.username,
            firstname: user.firstname,
            lastname: user.lastname,
            active: user.active,
            roleIds: user.roles?.map(el => el.id),
            cityIds: user.cities?.map(el => el.id),
            productIds: user.products?.map(el => el.id)
        });
        setOpen(true);
    };

    const onResetPasswordClick = (user: User) => {
        setResetPasswordUser(user);
        setResetPasswordOpen(true);
    };

    const saveUser = () => {
        const payload = {
            active: selectedUser.active,
            roleIds: selectedUser.roleIds.map(Number),
            cityIds: selectedUser.cityIds.map(Number),
            productIds: selectedUser.productIds.map(Number)
        };

        editUser(selectedUser.id, payload)
            .then(() => {
                setOpen(false);
                getUsers();
            })
            .finally(() => setSelectedUser(initialUserState));
    };

    return (
        <>
            <ResetPasswordModal
                open={resetPasswordOpen}
                onClose={() => setResetPasswordOpen(false)}
                user={resetPasswordUser}
            />
            <UserModal
                open={open}
                onClose={() => setOpen(false)}
                onSave={saveUser}
                roles={roles}
                cities={cities}
                products={products}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
            />

            <UserTable
                users={users}
                canEdit={canEdit}
                onEdit={onEditClick}
                onResetPassword={onResetPasswordClick}
            />
        </>
    );
};

export default Users;
